import React, { FC, useState, useEffect } from "react";
import { TNumber } from "../../../interfaces/common";
import { HandleIcon, colors } from "@commonsku/styles";
import { MetadataProperties } from "../../../interfaces/Metadata";
import MetadataAdjustment from "./MetadataAdjustment";

interface MetaDataListProps {
  metaDataId: string;
  label: string;
  type: string;
  parent_type: string;
  properties?: MetadataProperties;
  item_id: string;
  item_retail_adjustment_id?: string;
  hasOptionAdjustment: boolean;
  adjustment?: string;
  mandatory: TNumber;
  updateMetadataList: (
    metaDataId: string,
    key: string,
    value?: string | number | object
  ) => (value: string | number | object) => void;
  deleteMetaDataList: (metaDataId: string) => void;
  editMetaDataList: (metaDataId: string) => void;
  onAddRetailAdjustment: (item_id: string, selector: string, value: any) => void;
  onUpdateRetailAdjustment: (item_retail_adjustment_id: string, selector: string, prevValue: any, value: any) => void;
}

const MetaDataList = React.forwardRef<HTMLTableRowElement, MetaDataListProps>(({
  metaDataId,
  label,
  type,
  parent_type,
  properties,
  item_id,
  item_retail_adjustment_id,
  hasOptionAdjustment,
  adjustment,
  mandatory,
  updateMetadataList,
  deleteMetaDataList,
  editMetaDataList,
  onAddRetailAdjustment,
  onUpdateRetailAdjustment,
}, ref) => {
  const [characterLimitValue, setCharacterLimitValue] = useState(properties?.characterLimit ?? "");
  const [hasCharacterLimitInputError, setHasCharacterLimitInputError] = useState(false);
  const [hasAdjustmentInputError, setHasAdjustmentInputError] = useState(false);

  useEffect(() => {
    setCharacterLimitValue(properties?.characterLimit ?? "");
  }, [properties]);

  return (
    <>
      <tr
        ref={ref}
        style={{ marginTop: 4, marginBottom: 4 }}
        data-test-id={`metadata-list-item-${metaDataId}`}
      >
        <td className="align-self-center">{metaDataId ? <HandleIcon color="#DAE9EE" altText={label || ""} /> : ""}</td>
        <td>{label}</td>
        <td>
          <select
            value={type}
            style={{ margin: 0 }}
            onChange={(e) => {
              updateMetadataList(metaDataId, "type", type)(e.target.value);
              e.target.value === "SELECT" && editMetaDataList(metaDataId);
            }}
            data-test-id={`metadata-type-${metaDataId}`}
          >
            <option value="TEXT">Text Field</option>
            <option value="SELECT">Select Field</option>
          </select>
        </td>
        {parent_type === "ITEM" && (
          <td {...(type === "SELECT" && {
            style: { textAlign: "center" }
          })}>
            {type === "TEXT" ? (
              <input
                type="text"
                inputMode="numeric"
                value={characterLimitValue}
                style={{ margin: 0 }}
                onFocus={e => e.target.select()}
                onChange={(e) => {
                  const newCharacterLimitValue = e.target.value.replace(/[^0-9]/g, "");
                  setCharacterLimitValue(newCharacterLimitValue);
                }}
                onBlur={(e) => {
                  const inputValue = e.target.value.replace(/^0+/, "");
                  const newProperties = { ...properties};

                  if (inputValue === "") {
                    delete newProperties.characterLimit;
                  } else if (!isNaN(Number(inputValue)) && isFinite(Number(inputValue))) {
                    newProperties.characterLimit = Number(inputValue);
                  } else {
                    setHasCharacterLimitInputError(true);
                    return;
                  }

                  setHasCharacterLimitInputError(false);
                  updateMetadataList(
                    metaDataId,
                    "properties",
                    properties
                  )(newProperties);
                }}
                data-test-id={`metadata-character-limit-input-${metaDataId}`}
              />
            ) : (
              <span>N/A</span>
            )}
          </td>
        )}
        {parent_type === "ITEM" && (
          <td>
            {type === "TEXT" ? (
              <MetadataAdjustment
                metadataId={metaDataId}
                adjustment={adjustment}
                parentId={item_id}
                itemRetailAdjustmentId={item_retail_adjustment_id}
                onUpdateRetailAdjustment={onUpdateRetailAdjustment}
                onAddRetailAdjustment={onAddRetailAdjustment}
                onAdjustmentError={setHasAdjustmentInputError}
              />
            ) : hasOptionAdjustment ? (
              <a
                onClick={(e) => {
                  e.preventDefault();
                  editMetaDataList(metaDataId);
                }}
                data-test-id={`metadata-custom-upcharges-button-${metaDataId}`}
              >
                Custom Upcharges
              </a>
            ) : (
              <span></span>
            )}
          </td>
        )}
        <td>
          <input
            type="checkbox"
            checked={mandatory == 1 ? true : false}
            style={{ margin: 0 }}
            onChange={(e) =>
              updateMetadataList(
                metaDataId,
                "mandatory",
                mandatory
              )(e.target.checked ? 1 : 0)
            }
            data-test-id={`metadata-mandatory-checkbox-${metaDataId}`}
          />
        </td>
        <td>
          <a
            onClick={(e) => {
              e.preventDefault();
              deleteMetaDataList(metaDataId);
            }}
            data-test-id={`metadata-delete-button-${metaDataId}`}
          >
            Delete
          </a>
          <br />
          {type === "SELECT" && (
            <a
              onClick={(e) => {
                e.preventDefault();
                editMetaDataList(metaDataId);
              }}
              data-test-id={`metadata-edit-button-${metaDataId}`}
            >
              Edit
            </a>
          )}
        </td>
      </tr>
      {parent_type === "ITEM" && (hasCharacterLimitInputError || hasAdjustmentInputError) && (
        <tr
          data-test-id={`metadata-list-item-error-${metaDataId}`}
        >
          <td/>
          <td/>
          <td/>
          <td style={{ color: colors.errors.main }}> {hasCharacterLimitInputError && "Please enter a valid number"}</td>
          <td style={{ color: colors.errors.main }}> {hasAdjustmentInputError && "Please enter a valid number"}</td>
          <td/>
          <td/>
        </tr>
      )}
    </>
  );
});

export default MetaDataList;
